import { useAppStore } from '@core/store'
import { latestParsed, type LoadedBcf } from '../store/bcfClientSlice'
import { bcfAudit } from './bcfAudit'
import { confirmar } from '@core/layout/janela-destacada/abrirJanela'
import { formatarInteiro } from './bcfAgrupar'

/**
 * Importa um `.bcfzip` (ou `.bcf`) para a biblioteca do módulo BCF.
 *
 * É a mesma importação do botão "+ Importar BCF" (BcfLibraryPage) e do
 * arrastar um BCF para o 3D (viewer/canvas/DropZone.tsx): um BCF com o
 * mesmo ProjectId de um que já está na biblioteca pode entrar como versão
 * nova dele (`pairedTo`) ou ficar como BCF separado: pergunta-se, porque
 * um ficheiro parcial (por exemplo, só os conflitos críticos) como versão
 * nova deixava a versão actual só com esses tópicos. Senão, é um BCF
 * novo. Regista a importação na
 * auditoria do módulo. Um ficheiro que não se lê devolve `entry` a null,
 * com o erro em `bcfImportError`, como antes.
 */
export async function importarBcfNaBiblioteca(
  file: File,
): Promise<{ entry: LoadedBcf | null; pairedTo: string | null }> {
  const { entry, pairedTo } = await useAppStore.getState().bcfImportSmart(file, {
    versaoNova: (alvo) => confirmar(mensagemVersaoNova(file.name, alvo)),
  })
  if (!entry) return { entry: null, pairedTo: null }
  bcfAudit('bcf.import', {
    filename: file.name,
    size_bytes: file.size,
    topic_count: latestParsed(entry).topics.length,
    bcf_version: latestParsed(entry).version,
    paired_to: pairedTo ?? null,
  })
  return { entry, pairedTo }
}

/** A pergunta "versão nova ou BCF separado". OK junta como versão nova. */
export function mensagemVersaoNova(
  ficheiro: string,
  alvo: { nome: string; topicosActuais: number; topicosDoFicheiro: number },
): string {
  const t = (n: number) => `${formatarInteiro(n)} ${n === 1 ? 'tópico' : 'tópicos'}`
  return (
    `"${ficheiro}" é do mesmo projeto BCF que "${alvo.nome}" ` +
    `(versão atual com ${t(alvo.topicosActuais)}; o ficheiro tem ${t(alvo.topicosDoFicheiro)}).

` +
    `OK: entra como versão nova de "${alvo.nome}" (a versão atual fica no histórico).
` +
    'Cancelar: fica como BCF separado na biblioteca.'
  )
}
